import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Image as ImageIcon, Building2, Link2, Unlink, ArrowUpRight, Trash2, UserPlus } from 'lucide-react';
import Modal from '../../components/Modal';
import TypeaheadSelect from '../../components/TypeaheadSelect';
import {
  getAccountHierarchy,
  setAccountParent,
  updateAccountHierarchyPosition,
  addAccountPlaceholder,
  updateAccountPlaceholder,
  deleteAccountPlaceholder,
  promoteAccountPlaceholder,
  getAccounts,
} from '../../api';
import AccountMapCanvas, { layoutHierarchy, connectorPath, NODE_W, NODE_H } from './AccountMapCanvas';
import './OrgCanvas.css';

// ─── PNG export ─────────────────────────────────────────────────────────────
function escapeXml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}
function truncate(str, max) {
  const s = String(str || '');
  return s.length > max ? s.slice(0, max - 1) + '…' : s;
}

function buildExportSvg(hierarchy) {
  const { nodes, byKey, positions, width, height } = layoutHierarchy(hierarchy);
  const F = 'font-family="Segoe UI, Arial, sans-serif"';
  let edges = '';
  nodes.forEach(n => {
    if (n.parentKey && byKey.has(n.parentKey)) {
      const d = connectorPath(positions.get(n.parentKey), positions.get(n.key));
      edges += `<path d="${d}" stroke="#94a3b8" stroke-width="2" fill="none"/>`;
    }
  });
  let cards = '';
  nodes.forEach(n => {
    const p = positions.get(n.key);
    const placeholder = n.type === 'placeholder';
    const stroke = n.isCurrent ? '#2563eb' : (placeholder ? '#f59e0b' : '#9aa6b4');
    const fill = placeholder ? '#fffbeb' : '#ffffff';
    const dash = placeholder ? ' stroke-dasharray="5 3"' : '';
    cards += `<rect x="${p.x}" y="${p.y}" width="${NODE_W}" height="${NODE_H}" rx="6" fill="${fill}" stroke="${stroke}" stroke-width="${n.isCurrent ? 2.5 : 1.5}"${dash}/>`;
    cards += `<text x="${p.x + 14}" y="${p.y + 28}" ${F} font-size="13.5" font-weight="700" fill="#1f2933">${escapeXml(truncate(n.name, 24))}</text>`;
    if (n.sublabel) {
      cards += `<text x="${p.x + 14}" y="${p.y + 48}" ${F} font-size="11" fill="#64748b">${escapeXml(truncate(n.sublabel, 28))}</text>`;
    }
    if (n.isCurrent) {
      cards += `<text x="${p.x + NODE_W - 14}" y="${p.y + 18}" text-anchor="end" ${F} font-size="9.5" font-weight="700" fill="#2563eb">VIEWING</text>`;
    }
  });
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><rect width="${width}" height="${height}" fill="#f8fafc"/>${edges}${cards}</svg>`;
  return { svg, width, height };
}

function downloadMapAsPng(hierarchy, accountName) {
  const { svg, width, height } = buildExportSvg(hierarchy);
  const img = new Image();
  img.onload = () => {
    const scale = 2;
    const cv = document.createElement('canvas');
    cv.width = Math.round(width * scale);
    cv.height = Math.round(height * scale);
    const ctx = cv.getContext('2d');
    ctx.scale(scale, scale);
    ctx.drawImage(img, 0, 0);
    cv.toBlob(blob => {
      if (!blob) { alert('Could not generate image.'); return; }
      const safe = (accountName || 'account').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `account-map-${safe}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    }, 'image/png');
  };
  img.onerror = () => alert('Could not render the map image.');
  img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
}

// ─── Tab component ──────────────────────────────────────────────────────────
function AccountHierarchyMapTab({ account, canDo, reloadAccount }) {
  const navigate = useNavigate();
  const [hierarchy, setHierarchy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [allAccounts, setAllAccounts] = useState([]);

  // Modals
  const [menuNode, setMenuNode] = useState(null);       // node action modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [promoteNode, setPromoteNode] = useState(null);

  const canEdit = canDo('accounts', 'edit');
  const canCreate = canDo('accounts', 'create');

  const load = useCallback(async () => {
    try {
      const res = await getAccountHierarchy(account.id);
      setHierarchy(res.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load account map');
    } finally {
      setLoading(false);
    }
  }, [account.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Account list for the parent/attach pickers. Non-fatal if it fails.
    getAccounts().then(res => setAllAccounts(res.data?.accounts || res.data || [])).catch(() => {});
  }, []);

  // Replace local state with a server response and refresh the Overview tab
  // (parent/subsidiaries may have changed).
  const applyResponse = useCallback((res, { structural = false } = {}) => {
    setHierarchy(res.data);
    if (structural && reloadAccount) reloadAccount();
  }, [reloadAccount]);

  const withError = useCallback(async (fn) => {
    try { setError(''); await fn(); }
    catch (err) { setError(err.response?.data?.error || 'Action failed'); }
  }, []);

  // ── Canvas callbacks ─────────────────────────────────────────────────────
  const onMoveNode = useCallback((node, x, y) => withError(async () => {
    if (node.type === 'placeholder') {
      applyResponse(await updateAccountPlaceholder(node.id, { x, y }));
    } else {
      applyResponse(await updateAccountHierarchyPosition(node.id, x, y));
    }
  }), [withError, applyResponse]);

  const onConnect = useCallback((parentNode, childNode) => withError(async () => {
    if (childNode.type === 'placeholder') {
      applyResponse(await updateAccountPlaceholder(childNode.id, { parent_account_id: parentNode.id }), { structural: true });
    } else {
      applyResponse(await setAccountParent(childNode.id, parentNode.id), { structural: true });
    }
  }), [withError, applyResponse]);

  const onNodeClick = useCallback((node) => { setMenuNode(node); }, []);

  // ── Pickers data ─────────────────────────────────────────────────────────
  const familyAccountIds = useMemo(
    () => new Set((hierarchy?.accounts || []).map(a => a.id)),
    [hierarchy]
  );
  // Accounts that can be attached as a NEW child into this family.
  const attachableAccounts = useMemo(
    () => allAccounts.filter(a => !familyAccountIds.has(a.id)).map(a => ({ id: a.id, name: a.name })),
    [allAccounts, familyAccountIds]
  );
  // Accounts eligible to be a node's parent (any account except itself; server
  // guards against descendants/cycles).
  const parentOptionsFor = useCallback((nodeId) =>
    allAccounts.filter(a => a.id !== nodeId).map(a => ({ id: a.id, name: a.name })),
    [allAccounts]
  );

  if (loading) return <div className="am-loading">Loading account map…</div>;

  return (
    <div className="am-tab">
      <div className="am-toolbar">
        <div className="am-toolbar-info">
          <Building2 size={16} />
          <span>Corporate family — rooted at the topmost parent. The account you're viewing is highlighted.</span>
        </div>
        <div className="am-toolbar-actions">
          {canEdit && (
            <button className="btn btn-secondary btn-sm" onClick={() => setShowAddModal(true)}>
              <Plus size={15} /> Add subsidiary
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => hierarchy && downloadMapAsPng(hierarchy, account.name)}>
            <ImageIcon size={15} /> Export PNG
          </button>
          <button className="btn btn-icon btn-sm" title="Refresh" onClick={load}>
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="am-canvas-scroll">
        <AccountMapCanvas
          hierarchy={hierarchy}
          canEdit={canEdit}
          onMoveNode={onMoveNode}
          onNodeClick={onNodeClick}
          onConnect={onConnect}
        />
      </div>

      {/* Node action modal */}
      <NodeMenuModal
        node={menuNode}
        onClose={() => setMenuNode(null)}
        canEdit={canEdit}
        canCreate={canCreate}
        navigate={navigate}
        parentOptionsFor={parentOptionsFor}
        onSetParent={(nodeId, parentId) => withError(async () => {
          applyResponse(await setAccountParent(nodeId, parentId), { structural: true });
          setMenuNode(null);
        })}
        onRenamePlaceholder={(pid, name) => withError(async () => {
          applyResponse(await updateAccountPlaceholder(pid, { name }));
          setMenuNode(null);
        })}
        onDeletePlaceholder={(pid) => withError(async () => {
          applyResponse(await deleteAccountPlaceholder(pid), { structural: true });
          setMenuNode(null);
        })}
        onPromote={(node) => { setMenuNode(null); setPromoteNode(node); }}
      />

      {/* Add subsidiary modal */}
      {showAddModal && (
        <AddSubsidiaryModal
          onClose={() => setShowAddModal(false)}
          familyAccounts={hierarchy?.accounts || []}
          defaultParentId={account.id}
          attachableAccounts={attachableAccounts}
          onAttachExisting={(childId, parentId) => withError(async () => {
            applyResponse(await setAccountParent(childId, parentId), { structural: true });
            setShowAddModal(false);
          })}
          onAddPlaceholder={(parentId, name) => withError(async () => {
            applyResponse(await addAccountPlaceholder(parentId, { name }), { structural: true });
            setShowAddModal(false);
          })}
        />
      )}

      {/* Promote placeholder modal */}
      {promoteNode && (
        <PromotePlaceholderModal
          node={promoteNode}
          onClose={() => setPromoteNode(null)}
          onPromote={(data) => withError(async () => {
            applyResponse(await promoteAccountPlaceholder(promoteNode.id, data), { structural: true });
            setPromoteNode(null);
          })}
        />
      )}
    </div>
  );
}

// ─── Node action modal ──────────────────────────────────────────────────────
function NodeMenuModal({ node, onClose, canEdit, canCreate, navigate, parentOptionsFor, onSetParent, onRenamePlaceholder, onDeletePlaceholder, onPromote }) {
  const [parentId, setParentId] = useState('');
  const [parentName, setParentName] = useState('');
  const [renameVal, setRenameVal] = useState('');
  useEffect(() => { setParentId(''); setParentName(''); setRenameVal(node?.name || ''); }, [node]);
  if (!node) return null;
  const isAccount = node.type === 'account';
  return (
    <Modal isOpen={!!node} onClose={onClose} title={node.name} size="sm">
      <div className="modal-body am-menu">
        {isAccount && (
          <>
            <button className="am-menu-item" onClick={() => { onClose(); navigate(`/accounts/${node.id}`); }}>
              <ArrowUpRight size={16} /> Open account
            </button>
            {canEdit && (
              <>
                <div className="am-menu-section">
                  <label>Set / change parent</label>
                  <TypeaheadSelect
                    id="am-set-parent"
                    placeholder="Search accounts…"
                    items={parentOptionsFor(node.id)}
                    value={parentId}
                    displayValue={parentName}
                    onChange={(id, name) => { setParentId(id); setParentName(name); }}
                  />
                  <button className="btn btn-primary btn-sm am-menu-apply" disabled={!parentId}
                    onClick={() => onSetParent(node.id, parentId)}>
                    <Link2 size={14} /> Set parent
                  </button>
                </div>
                {!node.isRoot && (
                  <button className="am-menu-detach" onClick={() => onSetParent(node.id, null)}>
                    <Unlink size={14} /> Detach from parent
                  </button>
                )}
              </>
            )}
          </>
        )}
        {!isAccount && (
          <>
            {canEdit && (
              <div className="am-menu-section">
                <label>Rename placeholder</label>
                <input className="form-input" value={renameVal} onChange={(e) => setRenameVal(e.target.value)} />
                <button className="btn btn-primary btn-sm am-menu-apply" disabled={!renameVal.trim()}
                  onClick={() => onRenamePlaceholder(node.id, renameVal.trim())}>
                  Save name
                </button>
              </div>
            )}
            {canCreate && (
              <button className="am-menu-item" onClick={() => onPromote(node)}>
                <UserPlus size={16} /> Promote to real account
              </button>
            )}
            {canEdit && (
              <button className="am-menu-item am-menu-danger" onClick={() => onDeletePlaceholder(node.id)}>
                <Trash2 size={16} /> Delete placeholder
              </button>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

// ─── Add subsidiary modal ───────────────────────────────────────────────────
function AddSubsidiaryModal({ onClose, familyAccounts, defaultParentId, attachableAccounts, onAttachExisting, onAddPlaceholder }) {
  const [mode, setMode] = useState('existing'); // 'existing' | 'placeholder'
  const [parentId, setParentId] = useState(defaultParentId);
  const [childId, setChildId] = useState('');
  const [childName, setChildName] = useState('');
  const [placeholderName, setPlaceholderName] = useState('');

  const parentItems = familyAccounts.map(a => ({ id: a.id, name: a.name }));
  const parentDisplay = familyAccounts.find(a => a.id === parentId)?.name || '';

  return (
    <Modal isOpen onClose={onClose} title="Add subsidiary" size="md">
      <div className="modal-body">
        <div className="am-mode-tabs">
          <button className={`btn btn-sm ${mode === 'existing' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setMode('existing')}>Existing account</button>
          <button className={`btn btn-sm ${mode === 'placeholder' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setMode('placeholder')}>New placeholder</button>
        </div>

        <div className="form-group">
          <label className="form-label">Parent account</label>
          <select className="form-select" value={parentId} onChange={(e) => setParentId(parseInt(e.target.value, 10))}>
            {parentItems.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        {mode === 'existing' ? (
          <>
            <div className="form-group">
              <label className="form-label">Account to attach as a subsidiary</label>
              <TypeaheadSelect
                id="am-attach-account"
                placeholder="Search accounts…"
                items={attachableAccounts}
                value={childId}
                displayValue={childName}
                onChange={(id, name) => { setChildId(id); setChildName(name); }}
              />
              <p className="am-hint">Only accounts not already in this family are listed.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" disabled={!childId || !parentId}
                onClick={() => onAttachExisting(childId, parentId)}>Attach</button>
            </div>
          </>
        ) : (
          <>
            <div className="form-group">
              <label className="form-label">Placeholder name</label>
              <input className="form-input" value={placeholderName} autoFocus
                onChange={(e) => setPlaceholderName(e.target.value)}
                placeholder="e.g. Acme EMEA (not yet in CRM)" />
              <p className="am-hint">A stand-in for a subsidiary not yet in the CRM. Promote it to a real account later.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" disabled={!placeholderName.trim() || !parentId}
                onClick={() => onAddPlaceholder(parentId, placeholderName.trim())}>Add placeholder</button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ─── Promote placeholder modal ──────────────────────────────────────────────
function PromotePlaceholderModal({ node, onClose, onPromote }) {
  const [form, setForm] = useState({ industry: '', website: '', country: '', state: '' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <Modal isOpen onClose={onClose} title={`Promote "${node.name}" to an account`} size="md">
      <div className="modal-body">
        <p className="am-hint">Creates a real CRM account named "{node.name}" as a child of its current parent, then removes the placeholder.</p>
        <div className="form-group">
          <label className="form-label">Industry</label>
          <input className="form-input" value={form.industry} onChange={(e) => set('industry', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Website</label>
          <input className="form-input" type="url" value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://…" />
        </div>
        <div className="form-row" style={{ display: 'flex', gap: '12px' }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">State / Region</label>
            <input className="form-input" value={form.state} onChange={(e) => set('state', e.target.value)} />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Country</label>
            <input className="form-input" value={form.country} onChange={(e) => set('country', e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onPromote(form)}>Promote</button>
        </div>
      </div>
    </Modal>
  );
}

export default AccountHierarchyMapTab;
